import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Pagination,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import SearchIcon from
  "@mui/icons-material/Search";

import {
  useDispatch,
  useSelector,
} from "react-redux";

import {
  setCustomers,
} from "../../features/costumerSlice";

import {
  GetUsersPaged,
  UpdatePermanentDiscount,
} from "../../utils/usersAPI";

import Swal from "sweetalert2";

const PAGE_SIZE = 50;

const CustomersList = () => {
  const dispatch = useDispatch();

  /*
   * כאן נשמרים רק 50 הלקוחות
   * שהתקבלו עבור העמוד הנוכחי.
   */
  const customers = useSelector(
    (state) =>
      state.customers.customers || []
  );

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    error,
    setError,
  ] = useState("");

  /*
   * מספר העמוד בשרת מתחיל מ־1.
   */
  const [
    page,
    setPage,
  ] = useState(1);

  const [
    totalCustomers,
    setTotalCustomers,
  ] = useState(0);

  const [
    totalPages,
    setTotalPages,
  ] = useState(0);

  /*
   * הערך שהמנהל מקליד.
   */
  const [
    searchInput,
    setSearchInput,
  ] = useState("");

  /*
   * הערך שנשלח בפועל לשרת.
   */
  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");

  /*
   * טעינת 50 לקוחות בלבד מהשרת.
   */
  const loadCustomers =
    useCallback(async () => {
      try {
        setLoading(true);
        setError("");

        const data =
          await GetUsersPaged({
            page,
            search: searchTerm,
          });

        dispatch(
          setCustomers(
            data.items || []
          )
        );

        setTotalCustomers(
          Number(data.total || 0)
        );

        setTotalPages(
          Number(data.totalPages || 0)
        );
      } catch (err) {
        console.error(
          "שגיאה בטעינת לקוחות:",
          err
        );

        dispatch(setCustomers([]));
        setTotalCustomers(0);
        setTotalPages(0);

        if (
          err?.response?.status === 401
        ) {
          setError(
            "אין הרשאה לצפות בלקוחות. יש להתחבר מחדש כמנהל."
          );
        } else if (
          err?.response?.status === 403
        ) {
          setError(
            "המשתמש המחובר אינו מנהל."
          );
        } else {
          setError(
            err?.response?.data?.message ||
            "אירעה שגיאה בטעינת הלקוחות."
          );
        }
      } finally {
        setLoading(false);
      }
    }, [
      dispatch,
      page,
      searchTerm,
    ]);

  /*
   * קריאה חדשה לשרת בכל שינוי
   * של עמוד או חיפוש.
   */
  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  /*
   * חיפוש מתבצע רק בלחיצה
   * על הכפתור או על Enter.
   */
  const handleSearch = () => {
    setSearchTerm(
      searchInput.trim()
    );

    setPage(1);
  };

  const handleSearchKeyDown = (
    event
  ) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setPage(1);
  };

  /*
   * עדכון הנחת לקוח קבועה.
   */
  const handlePermanentDiscountChange =
    async (customer, checked) => {
      try {
        await UpdatePermanentDiscount(
          customer.id,
          checked
        );

        dispatch(
          setCustomers(
            customers.map(
              (currentCustomer) =>
                currentCustomer.id ===
                customer.id
                  ? {
                      ...currentCustomer,
                      hasPermanentDiscount:
                        checked,
                    }
                  : currentCustomer
            )
          )
        );
      } catch (err) {
        console.error(
          "שגיאה בעדכון הנחה:",
          err
        );

        Swal.fire({
          icon: "error",
          title: "עדכון ההנחה נכשל",
          text:
            err?.response?.data?.message ||
            "לא ניתן לעדכן את הנחת הלקוח.",
        });
      }
    };

  /*
   * יצירת טקסט הכתובת.
   */
  const formatUserAddress = (
    customer
  ) => {
    const address =
      customer?.userAddresses?.[0];

    if (!address) {
      return "לא הוגדרה כתובת";
    }

    const addressParts = [
      address.city,
      address.street,
      address.houseNumber,
    ].filter(Boolean);

    return addressParts.length > 0
      ? addressParts.join(" ")
      : "לא הוגדרה כתובת";
  };

  /*
   * מספר הלקוח הראשון והאחרון
   * שמוצגים בעמוד הנוכחי.
   */
  const firstDisplayedCustomer =
    totalCustomers === 0
      ? 0
      : (page - 1) * PAGE_SIZE + 1;

  const lastDisplayedCustomer =
    Math.min(
      page * PAGE_SIZE,
      totalCustomers
    );

  return (
    <Box
      dir="rtl"
      sx={{
        flexGrow: 1,
        padding: 3,
      }}
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography
            variant="h4"
            gutterBottom
          >
            לקוחות
          </Typography>

          {/* חיפוש */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              my: 2,
              alignItems: "flex-start",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
            }}
          >
            <TextField
              label="חיפוש לפי שם, מייל או טלפון"
              variant="outlined"
              fullWidth
              value={searchInput}
              onChange={(event) =>
                setSearchInput(
                  event.target.value
                )
              }
              onKeyDown={
                handleSearchKeyDown
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />

            <Button
              variant="contained"
              onClick={handleSearch}
              sx={{
                minWidth: 110,
                height: 56,
              }}
            >
              חיפוש
            </Button>

            <Button
              variant="outlined"
              color="error"
              onClick={
                handleClearSearch
              }
              disabled={
                !searchInput &&
                !searchTerm
              }
              sx={{
                minWidth: 110,
                height: 56,
              }}
            >
              ניקוי
            </Button>
          </Box>

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <Paper sx={{ padding: 2 }}>
            {loading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                sx={{
                  p: 5,
                  gap: 2,
                }}
              >
                <CircularProgress
                  size={30}
                />

                <Typography variant="h6">
                  טוען לקוחות...
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                variant="outlined"
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        ID
                      </TableCell>

                      <TableCell>
                        שם
                      </TableCell>

                      <TableCell>
                        אימייל
                      </TableCell>

                      <TableCell>
                        טלפון
                      </TableCell>

                      <TableCell>
                        כתובת
                      </TableCell>

                      <TableCell>
                        הנחת לקוח 5%
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {customers.map(
                      (customer) => (
                        <TableRow
                          key={customer.id}
                          hover
                        >
                          <TableCell>
                            {customer.id}
                          </TableCell>

                          <TableCell>
                            {customer.name}
                          </TableCell>

                          <TableCell>
                            {customer.email}
                          </TableCell>

                          <TableCell>
                            {customer.phone}
                          </TableCell>

                          <TableCell>
                            {formatUserAddress(
                              customer
                            )}
                          </TableCell>

                          <TableCell>
                            <Switch
                              checked={Boolean(
                                customer
                                  .hasPermanentDiscount
                              )}
                              onChange={(
                                event
                              ) =>
                                handlePermanentDiscountChange(
                                  customer,
                                  event
                                    .target
                                    .checked
                                )
                              }
                              color="secondary"
                              inputProps={{
                                "aria-label":
                                  `הנחת 5 אחוז ללקוח ${customer.name}`,
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )
                    )}

                    {customers.length ===
                      0 && (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          align="center"
                          sx={{ py: 5 }}
                        >
                          {searchTerm
                            ? "לא נמצאו לקוחות שמתאימים לחיפוש"
                            : "אין לקוחות להצגה"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>

                {/* אזור חלוקה לעמודים */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    px: 2,
                    py: 2,
                    borderTop: 1,
                    borderColor:
                      "divider",
                    backgroundColor:
                      "#fafafa",
                    flexWrap: "wrap",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 600,
                    }}
                  >
                    מציג לקוחות{" "}
                    {
                      firstDisplayedCustomer
                    }
                    –
                    {
                      lastDisplayedCustomer
                    }{" "}
                    מתוך{" "}
                    {totalCustomers}
                  </Typography>

                  {totalPages > 1 && (
                    <Box dir="ltr">
                      <Pagination
                        count={
                          totalPages
                        }
                        page={page}
                        onChange={(
                          event,
                          selectedPage
                        ) =>
                          setPage(
                            selectedPage
                          )
                        }
                        color="primary"
                        shape="rounded"
                        showFirstButton
                        showLastButton
                        siblingCount={1}
                        boundaryCount={1}
                      />
                    </Box>
                  )}
                </Box>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default CustomersList;